<script setup lang="ts">
import {formatDate} from "@/helpers/date.helper";
import {debounce} from "throttle-debounce";

import {computed, onMounted, ref, watch} from "vue";
import pulseBackend from "@/backend/pulse.backend";
import SmartText from "@/components/SmartText.vue";
import useAppToast from "@/composables/useAppToast";
import TimeoffType from "@/components/timeoff/TimeoffType.vue";
import PulseComments from "@/components/pulse/PulseComments.vue";
import PulseCommenter from "@/components/pulse/PulseCommenter.vue";
import PulsePRs from "@/components/pulse/PulsePRs.vue";
import PulseTimeAllocationPanel from "@/components/pulse/PulseTimeAllocationPanel.vue";
import PulseTimeAllocationDisplay from "@/components/pulse/PulseTimeAllocationDisplay.vue";
import WeeklyCheckinPromptDialog from "@/components/pulse/WeeklyCheckinPromptDialog.vue";
import {DateTime} from "luxon";
import {backend} from "@/helpers";

const {pulse, editable} = defineProps(['pulse', 'editable'])
const storageId = computed(() => `pulse-${pulse.date}`)

const toast = useAppToast()
const decoration = computed(() => {
  if (!pulse || pulse.editMode)
    return ''
  switch (pulse.type) {
    case 'PULSE':
      return '!bg-green-50 !border-green-200 dark:!bg-green-900/30 dark:!border-green-700';
    case 'NO_INFO':
      return '!bg-amber-50 !text-amber-600 !border-amber-200 dark:!bg-amber-900/30 dark:!text-amber-200 dark:!border-amber-700'
    case 'OOO':
    case 'HOLIDAY':
      return '!bg-surface-50 !text-muted-color dark:!bg-surface-700 dark:!border-surface-600';
    case 'FUTURE':
    case 'TODAY':
    default:
      return ''
  }

})

const timeAllocation = ref(pulse.timeAllocation || null);

const isTextValid = computed(() => {
  return pulse.newText && pulse.newText.trim().length > 0;
});

const isTimeAllocationValid = computed(() => {
  // Time allocation is now mandatory - require it to be set
  if (!timeAllocation.value) return false;
  
  const values = Object.values(timeAllocation.value) as number[];
  const total = values.reduce((sum: number, val: number) => sum + val, 0);
  return total === 100 && values.some(val => val > 0);
});

const isFormValid = computed(() => {
  return isTextValid.value && isTimeAllocationValid.value;
});

const timeAllocationTotal = computed(() => {
  if (!timeAllocation.value) return 0;
  const values = Object.values(timeAllocation.value) as number[];
  return values.reduce((sum: number, val: number) => sum + val, 0);
});

const saveButtonTooltip = computed(() => {  
  if (!isTextValid.value) return 'Pulse text cannot be empty';
  if (isFormValid.value) return 'Press Cmd+Enter to save';
  
  const total = timeAllocationTotal.value;
  if (total === 0) return 'Time allocation is required to save';
  if (total < 100) return `Time allocation total is ${total}%. Add ${100 - total}% more to save`;
  if (total > 100) return `Time allocation total is ${total}%. Remove ${total - 100}% to save`;
  return 'Time allocation must total 100% to save';
});

onMounted(() => {
  if (pulse && pulse.type === 'TODAY' && editable) {
    pulse.editMode = true
    pulse.newText = getLocal()
  }
})

const canEdit = computed(() => {
  return pulse && editable && ['PULSE', 'NO_INFO', 'TODAY'].includes(pulse.type)
})

function resetNewText() {
  const localText = getLocal();
  if (localText) {
    return localText
  }
  return pulse.type == 'PULSE' ? pulse.text : ''
}

const saving = ref(false)
const showCheckinPrompt = ref(false)

// Check if this pulse is for a Friday
const isFriday = computed(() => {
  if (!pulse?.date) return false;
  return DateTime.fromISO(pulse.date).weekday === 5;
});

async function checkAndPromptWeeklyCheckin(pulseDate: string, isNewPulse: boolean) {
  // Only prompt if it's Friday (weekday 5 in Luxon) and this is a new pulse submission
  const date = DateTime.fromISO(pulseDate);
  if (date.weekday !== 5 || !isNewPulse) {
    return;
  }

  try {
    // Get the start of the week for this pulse date
    const weekStart = date.startOf('week').toISODate();
    
    // Check if weekly check-in already exists
    const checkin = await backend.getSelfCheckin(weekStart);
    
    // Show prompt only if no check-in exists (missed or not yet submitted)
    if (!checkin || checkin.missed || !checkin.id) {
      showCheckinPrompt.value = true;
    }
  } catch (error) {
    console.error('Error checking weekly check-in:', error);
    // Don't show prompt if there's an error
  }
}

function edit() {
  pulse.editMode = true
  pulse.newText = resetNewText()
  
  // Strip out 'unallocated' category when entering edit mode
  // (backend adds this for pulses missing time allocation data)
  if (timeAllocation.value && 'unallocated' in timeAllocation.value) {
    const {unallocated, ...cleanAllocation} = timeAllocation.value as any;
    timeAllocation.value = cleanAllocation;
  }
}

watch(() => pulse.newText, () => {
  if (pulse.newText !== null)
    debounceStoreLocal()
})

const debounceStoreLocal = debounce(500, storeLocal)

function storeLocal() {
  localStorage.setItem(storageId.value, pulse.newText)
}

function resetLocal() {
  localStorage.removeItem(storageId.value)
}

function getLocal() {
  return localStorage.getItem(storageId.value)
}

async function save() {
  saving.value = true;
  const isNewPulse = !pulse.id; // Track if this is a new pulse before saving
  pulse.text = pulse.newText
  const pulseData = {
    ...pulse,
    // Always include timeAllocation since feature is now enabled
    timeAllocation: timeAllocation.value
  };
  const saved = await pulseBackend.post(pulseData)
  toast.success('Saved')
  pulse.id = saved.id
  pulse.type = saved.type
  pulse.timeAllocation = saved.timeAllocation
  pulse.editMode = false
  localStorage.removeItem(storageId.value)
  saving.value = false;
  
  // Check if we should prompt for weekly check-in (only on new Friday pulses)
  await checkAndPromptWeeklyCheckin(pulse.date, isNewPulse);
}

function cancel() {
  resetLocal()
  pulse.newText = resetNewText()
  pulse.editMode = false
}

const pulseCommenter = ref()
function showCommentPopover(e: any) {
  pulseCommenter.value.show(e, pulse)
}

function saveOnCmdEnter(e: KeyboardEvent) {
  if (e.key === 'Enter' && e.metaKey && isFormValid.value) {
    save();
    e.preventDefault();
  }
}

</script>

<template>
  <div>
  <Fieldset class="mt-8  group" :class="decoration"
            pt:legend:class="!border !border-surface-200 dark:!border-surface-700"
            pt:root:class="!px-2"
            pt:content:class="h-full"
            pt:contentContainer:class="h-full"
  >
    <template #legend>
      <div class="flex flex-row items-center" :id="pulse.id ? `legend-${pulse.id}` : ''">
        <slot name="legend">
          <div class="flex flex-row items-center ">
            <span class="font-bold">{{ formatDate(pulse.date, 'EEE, MMM dd') }}</span>
          </div>
        </slot>
        <i class="md:hidden md:group-hover:inline pi pi-pencil cursor-pointer hover:bg-surface-50 ml-2 p-1 rounded-md text-muted-color"
           v-if="canEdit && !pulse.editMode"
           @click="edit"
        />
      </div>

    </template>

    <div v-if="pulse" class="flex flex-col h-full">
      <PulseCommenter ref="pulseCommenter"/>
      <PulsePRs
        :prs-created="pulse.prsCreated || []"
        :prs-merged="pulse.prsMerged || []"
        class="pt-2"
      />
      <div class="p-2 grow" v-if="!pulse.editMode">
        <div v-if="pulse.type === 'PULSE'">
          <SmartText :text="pulse.text" @click="showCommentPopover"/>
          <PulseComments :pulse="pulse" v-if="pulse.commentCount" class="pt-2"/>
        </div>
        <TimeoffType v-else-if="pulse.type === 'OOO'" :value="pulse.text"/>
        <span v-else>{{ pulse.text }}</span>
      </div>
      <div v-else class="pt-2">
        <Textarea v-model="pulse.newText" rows="3" class="w-full" auto-resize @keydown="saveOnCmdEnter"/>
        <PulseTimeAllocationPanel
          v-model="timeAllocation"
          class="mt-4"
        />
        <div class="flex justify-end mt-2 gap-2">
          <Button 
            label="Save" 
            icon="pi pi-check" 
            @click="save" 
            :loading="saving" 
            :disabled="!isFormValid"
            v-tooltip="saveButtonTooltip"
          />
          <Button label="Cancel" icon="pi pi-times" @click="cancel" severity="secondary" outlined/>
        </div>
      </div>
      <div v-if="pulse.timeAllocation && !pulse.editMode" class="mt-3">
        <PulseTimeAllocationDisplay :time-allocation="pulse.timeAllocation" :show-legend="false" />
      </div>
    </div>
  </Fieldset>

  <!-- Weekly Check-in Prompt Dialog - Only render on Fridays -->
  <WeeklyCheckinPromptDialog v-if="isFriday" v-model:visible="showCheckinPrompt" />
  </div>

</template>
